import { ProcessMiningTypes } from "@inctec/smx-ui-client";
import axios, { Axios, AxiosRequestConfig, AxiosResponse } from "axios";
import { Paginated, Pagination, PaginationQuery } from "./common";
import { options } from "marked";

// Types for /process-mining/search/databases
export interface SearchDatabasesRequest {
  page: number;
  pageSize: number;
  databaseIds?: string[];
  search?: string;
}

export interface DatabaseDataQuality {
  uuid: string;
  name: string;
  description: string;
  status: "connected" | "error";
  overallScore: number;
  tableSyncStatus: "initial" | "reading" | "error" | "done";
  tablesCount: number;
  tablesAnalyzedCount: number;
  lastUpdated: string;
  fieldsAnalyzedCount: number;
  ticketsCount: number;
}

export interface SearchDatabasesResponse {
  page: number;
  pageSize: number;
  total: number;
  pagesTotal: number;
  result: DatabaseDataQuality[];
}

export function searchDatabases(
  data: SearchDatabasesRequest,
): Promise<AxiosResponse<SearchDatabasesResponse>> {
  return axios.post("/api/process-mining/search/databases", data);
}

export interface PutTableFieldKpiRequest {
  ignoreScore: boolean;
}

export function putTableFieldKpi(id: string, data: PutTableFieldKpiRequest) {
  return axios.put(
    `/api/process-mining/data-quality/table-fields/kpi/${id}`,
    data,
  );
}

// Types for /process-mining/search/tables
export interface DataQualityTablesRequest {
  page: number;
  pageSize: number;
  databaseId?: string;
  tableId?: string;
  search?: string;
}

export interface DataQualityTable {
  uuid: string;
  name: string;
  description: string;
  overallScore: number;
  records: number;
  lastUpdated: string;
  fieldsAnalyzedCount: number;
  ticketsCount: number;
  status: "error" | "done" | "analyzing" | "initial";
  deleted: boolean;
}

export interface TablesMetadata {
  tablesCount: number;
  averageScore: number;
  tablesWithIssues: number;
  ticketsCount: number;
}

export interface SearchTablesResponse {
  page: number;
  pageSize: number;
  total: number;
  pagesTotal: number;
  metadata: TablesMetadata;
  result: DataQualityTable[];
}

export function searchTables(
  data: DataQualityTablesRequest,
  options: AxiosRequestConfig = {},
): Promise<AxiosResponse<SearchTablesResponse>> {
  return axios.post("/api/process-mining/search/tables", data, options);
}

// Types for /process-mining/data-quality/tables/{id}
export type FilterCondition =
  | "equals"
  | "not_equals"
  | "greater_than"
  | "less_than"
  | "greater_than_or_equal"
  | "less_than_or_equal"
  | "contains"
  | "not_contains";

export interface KPIFilter {
  field: string;
  condition: FilterCondition;
  value: string | number | boolean;
}

export interface KPIFiltersInput {
  operator: "AND" | "OR";
  filters: KPIFilter[];
}

export type DataQualityField = ProcessMiningTypes.DatabaseTableField;
export interface GetDataQualityByIdResponse {
  uuid: string;
  name: string;
  description: string;
  overallScore: number;
  records: number;
  status: "analyzing" | "done" | "error" | "initial";
  lastUpdated: string;
  fieldsAnalyzedCount: number;
  ticketsCount: number;
  fields: DataQualityField[];
}

export function getDataQualityById(
  id: string,
): Promise<AxiosResponse<GetDataQualityByIdResponse>> {
  return axios.get(`/api/process-mining/data-quality/tables/${id}`);
}

export function postAnalyzeDataQuality(
  tableId: string,
): Promise<AxiosResponse<void>> {
  return axios.post(
    `/api/process-mining/data-quality/tables/${tableId}/analyze`,
  );
}

export function postSyncDatabaseTables(
  databaseId: string,
): Promise<AxiosResponse<void>> {
  return axios.post(
    `/api/process-mining/data-quality/databases/${databaseId}/sync`,
  );
}

export function postDataQualityFieldsData(
  fieldId: string,
  query: PaginationQuery & { qualityMetric: string },
): Promise<AxiosResponse<Paginated<Record<string, any>>>> {
  return axios.post(
    `/api/process-mining/data-quality/table-fields/${fieldId}/data`,
    query,
  );
}
